// Condition Library per-slot calculation types — the frontend twin of
// db/37_condition_library.sql's fn_ConditionCalcQuantity/fn_ConditionCalcTypeUnit.
// Keep both in sync if this vocabulary changes.
//
// Mirrors OST's own Condition Properties "Results" dropdown (Quantity1/2/3
// each independently pick from the SAME list for their Style), scoped down
// to what this platform can actually compute from a drawn polygon/polyline/
// placed point — OST's "backout area" and "attachment" options aren't
// modeled here and are deliberately dropped, not silently missing. OST's
// grid/tile results ARE modeled as of 2026-09-11 (tile_count_visible /
// tile_count_average / grid_length_visible — see src/utils/gridTakeoff.js)
// and read the type's GridTileWFt/GridTileHFt/GridGapIn (db/30).
//
// `volume` means different things per style (area: footprint x thickness,
// like a slab; linear: length x height x thickness, like a wall) — both
// use CalcType functions keyed by (style, value), not a prefixed code.

import { parseGeom } from "./scopeOptions";
import { gridTileTakeoff } from "../utils/gridTakeoff";

export const CALC_TYPES_BY_STYLE = {
  area: [
    { value: "area", label: "Area", unit: "SF" },
    { value: "perimeter", label: "Perimeter", unit: "LF" },
    { value: "volume", label: "Volume", unit: "CF" },
    { value: "tile_count_visible", label: "Tile count (visible)", unit: "EA" },
    { value: "tile_count_average", label: "Tile count (average)", unit: "EA" },
    { value: "grid_length_visible", label: "Grid length (visible)", unit: "LF" },
  ],
  linear: [
    { value: "length", label: "Length", unit: "LF" },
    { value: "segment_count", label: "Segment count", unit: "EA" },
    { value: "surface_single_side", label: "Surface area (single side)", unit: "SF" },
    { value: "surface_both_sides", label: "Surface area (both sides)", unit: "SF" },
    { value: "surface_top_or_bottom", label: "Surface area (top or bottom)", unit: "SF" },
    { value: "surface_top_and_bottom", label: "Surface area (top and bottom)", unit: "SF" },
    { value: "surface_single_end", label: "Surface area (single end)", unit: "SF" },
    { value: "surface_both_ends", label: "Surface area (both ends)", unit: "SF" },
    { value: "surface_all_side_duct", label: "Surface area (all sides/duct)", unit: "SF" },
    { value: "volume", label: "Volume", unit: "CF" },
  ],
  count: [
    { value: "count", label: "Count", unit: "EA" },
    { value: "perimeter_top_both_sides", label: "Perimeter (top + both sides)", unit: "LF" },
    { value: "perimeter_4_sides", label: "Perimeter (4 sides)", unit: "LF" },
    { value: "surface_single_width_side", label: "Surface area (single width side)", unit: "SF" },
    { value: "surface_both_width_sides", label: "Surface area (both width sides)", unit: "SF" },
  ],
};

// Appearance + sync-field vocabulary (2026-08-26, OST parity pass) — OST's
// Condition Properties dialog carries a line Pattern and a placement Shape
// alongside Color (see db/37_condition_library.sql's header comment on the
// Pattern/Shape columns). Pattern only affects a drawn Style='linear' run
// (TakeoffCanvas.jsx maps it to an SVG stroke-dasharray); Shape only affects
// a placed Style='count' symbol. Kept as small fixed lists (not free-typed)
// since the canvas only knows how to render these specific values. Moved
// here from ConditionLibrary.jsx (Phase D-3, see project memory — that
// admin page was retired) since EstimateCostLibrary.jsx's own "Drawable on
// Canvas" fields (Phase A) are now the only consumer.
// Pattern is one column (Items/Assemblies/TakeoffConditionLibrary.Pattern,
// NVARCHAR(20)) whose meaning depends on the row's Style, exactly like OST's
// own Condition Properties dialog: a LINEAR run's Pattern is its stroke dash
// (this list), an AREA's Pattern is its fill hatch (AREA_PATTERN_OPTIONS,
// added 2026-09-11 — until then Pattern was gated to linear only and every
// area drew the same flat translucent fill). A Count symbol has no pattern.
export const PATTERN_OPTIONS = [
  { value: "", label: "Solid" },
  { value: "dashed", label: "Dashed" },
  { value: "dotted", label: "Dotted" },
  { value: "dash-dot", label: "Dash-dot" },
];

// OST's area fill set, in its order. "" (NULL) stays the pre-existing
// translucent fill so every area drawn before this list existed renders
// unchanged — that is OST's "Transparent", so it's labeled as such. "none"
// is outline-only; "solid" is an opaque fill; the rest are hatches drawn in
// the condition's own Color (TakeoffCanvas.jsx's areaFillPattern()).
export const AREA_PATTERN_OPTIONS = [
  { value: "", label: "Transparent (default)" },
  { value: "none", label: "None (outline only)" },
  { value: "solid", label: "Solid" },
  { value: "horizontal", label: "Horizontal" },
  { value: "vertical", label: "Vertical" },
  { value: "backward-diag", label: "Backward Diag." },
  { value: "forward-diag", label: "Forward Diag." },
  { value: "crosshatch", label: "Crosshatch" },
  { value: "diag-crosshatch", label: "Diag. Crosshatch" },
];

// Which Pattern list a Style edits (null = the style has no pattern at all).
// A Count symbol's free-placed glyph (circle/rect/hexagon/light module…) is a
// filled shape too, so since 2026-09-11 it takes the same fill set an Area
// does — per direct feedback ("select a pattern regardless of style and
// shape … I want a cross hatched fill"). Door/window wedges keep their own
// rendering and ignore it.
export function patternOptionsForStyle(style) {
  if (style === "linear") return PATTERN_OPTIONS;
  if (style === "area" || style === "count") return AREA_PATTERN_OPTIONS;
  return null;
}

// Default color for a NEW drawable row — random from a palette of
// distinguishable colors rather than the same teal every time (per direct
// feedback, 2026-09-11), so consecutive types don't all land identical on the
// canvas. `avoid` = colors already used by other rows, skipped when possible.
export const DRAWABLE_COLOR_PALETTE = [
  "#0d9488", "#2563eb", "#dc2626", "#d97706", "#7c3aed", "#db2777", "#059669", "#ea580c",
  "#0891b2", "#4f46e5", "#65a30d", "#b45309", "#be123c", "#0369a1", "#9333ea", "#15803d",
];
export function randomDrawableColor(avoid = []) {
  const taken = new Set((avoid || []).map(c => String(c || "").toLowerCase()));
  const free = DRAWABLE_COLOR_PALETTE.filter(c => !taken.has(c));
  const pool = free.length ? free : DRAWABLE_COLOR_PALETTE;
  return pool[Math.floor(Math.random() * pool.length)];
}
// Full symbol catalog (2026-09-09) — expanded from the original 6-shape set
// per a reference legacy-tool symbol picker. Grouped exactly like that
// reference. WALL_SNAPPED_SHAPES lists the door/window family, which places
// jamb-to-jamb along a wall the same way `door` always has (see
// TakeoffCanvas.jsx's symbolGeometry()/onPointerDown's symbolMode branch);
// every other value here is free-placed — click anywhere in a room, no wall
// needed (TakeoffCanvas.jsx's SymbolGlyph() + roomContainingPolygon()).
export const SHAPE_OPTIONS = [
  { value: "", label: "Auto (from Syncs to)" },
  // Doors & windows (wall-snapped)
  { value: "door", label: "Door swing (RH/LH — select + press S to flip)" },
  { value: "double-door", label: "Double door" },
  { value: "double-egress-door", label: "Double egress door" },
  { value: "window", label: "Window 1" },
  { value: "window-2", label: "Window 2" },
  { value: "window-3", label: "Window 3" },
  { value: "bifold", label: "Bifold" },
  { value: "sliding-door", label: "Sliding door" },
  { value: "pass-through", label: "Pass-through (in a wall)" },
  // Basic markers (free-placed)
  { value: "rect", label: "Rectangle" },
  { value: "square", label: "Square" },
  { value: "circle", label: "Circle" },
  { value: "triangle", label: "Triangle" },
  { value: "diamond", label: "Diamond" },
  { value: "ellipse", label: "Ellipse" },
  { value: "hexagon", label: "Hexagon" },
  { value: "octagon", label: "Octagon" },
  { value: "check", label: "Check" },
  { value: "x", label: "X mark" },
  { value: "dot", label: "Dot" },
  // Fire Alarm (free-placed)
  { value: "fire-pull-station", label: "Fire Alarm: Pull Station" },
  { value: "smoke-detector", label: "Fire Alarm: Smoke Detector" },
  { value: "fire-control-panel", label: "Fire Alarm: Control Panel" },
  // Electrical (free-placed)
  { value: "outlet-single", label: "Electrical: Single Outlet" },
  { value: "outlet-duplex", label: "Electrical: Duplex Outlet" },
  { value: "switch", label: "Electrical: Switch" },
  // Ceiling systems (free-placed) — the shapes the HEPA & Lighting engine
  // generates (src/utils/autoLayoutEngine.js); also placeable by hand.
  // widthFt is the long side (2x4 = 4 ft); the 2x2 variants are square.
  { value: "hepa-filter", label: "Ceiling: HEPA filter 2x4" },
  { value: "hepa-filter-2x2", label: "Ceiling: HEPA filter 2x2" },
  { value: "light-2x4", label: "Ceiling: Light fixture 2x4" },
  { value: "light-2x2", label: "Ceiling: Light fixture 2x2" },
  // Mechanical (free-placed)
  { value: "fan-riser", label: "Fan (Riser)" },
  { value: "terminal-box", label: "Terminal Box" },
  { value: "temp-sensor", label: "Temperature Sensor" },
  { value: "humidistat", label: "Humidistat" },
  { value: "diffuser-supply-4way", label: "Supply Ceiling Diffuser (4-Way Blow)" },
  { value: "grille-return", label: "Return Ceiling Grille or Register" },
  { value: "diffuser-linear-supply", label: "Supply Linear Diffuser w/Plenum" },
  { value: "diffuser-linear-return", label: "Return Linear Diffuser w/Plenum" },
  { value: "flex-connection", label: "Flexible Connection" },
  // Plumbing (free-placed)
  { value: "valve-gate", label: "Shut-off Valve (Gate Valve)" },
  { value: "valve-ball", label: "Shut-off Valve (Ball Valve)" },
  { value: "valve-globe", label: "Globe Valve" },
];

// The door/window family places jamb-to-jamb along a wall (existing
// snapToNearestWall() model); everything else in SHAPE_OPTIONS is
// free-placed (click anywhere in a room). Checked by value, not by group
// position, so this stays correct regardless of SHAPE_OPTIONS' own order.
export const WALL_SNAPPED_SHAPES = new Set([
  "door", "double-door", "double-egress-door", "window", "window-2", "window-3", "bifold", "sliding-door",
  "pass-through",
]);

// The shape a Count type / placed row draws as: its own Shape, else a guess
// from its legacy-field tag. A pass-through sits IN a wall (most of them in
// the wall two rooms share), so a pass-through type with no Shape is
// wall-snapped like a door — it used to fall through to a free-placed dot,
// which never landed on a wall at all (issue #527). A type with an explicit
// Shape keeps it.
export function resolveSymbolShape(item) {
  if (item?.Shape) return item.Shape;
  if (item?.SyncsToCriteriaField === "door_count") return "door";
  if (item?.SyncsToCriteriaField === "window_count") return "window";
  if (item?.SyncsToCriteriaField === "pass_throughs") return "pass-through";
  return "dot";
}

// Per-room count fields (takeoffCriteria.js, 'arch' discipline) a Count-style
// condition can drive automatically — placing/removing a symbol of a type
// with one of these set keeps that field's value in sync (see
// sp_SaveTakeoffConditionDrawn's write-through). Kept as a small fixed list
// here rather than importing takeoffCriteria.js's full field set, since only
// these three make sense as a placed canvas symbol today.
export const SYNC_FIELD_OPTIONS = [
  { value: "", label: "— none —" },
  { value: "door_count", label: "Personnel Doors" },
  { value: "window_count", label: "Vision Panels" },
  { value: "pass_throughs", label: "Pass-Throughs" },
  // P0 Phase D (2026-09-12): tags the receptacle Room Element. A room carrying
  // an element of a type tagged here has its receptacle circuits generated
  // from the element's effective quantity (electricalCircuitEngine.js
  // roomDeviceCounts); the legacy per-room outlet_count criterion is only the
  // fallback for rooms without one. Placed symbols still write the legacy
  // field through (db/37) so un-migrated readers (room formulas' outletCount)
  // keep seeing a value.
  { value: "outlet_count", label: "Duplex Outlets (receptacle circuits)" },
  // Process drop Room Elements (P0 / Process Utilities Phase C, 2026-09-12):
  // tag the generic room-service drop element for each legacy drop criterion.
  // Room formulas (cdaDrops …) and the Utility demand view's reference list
  // read a room's tagged drop elements when it has any, else the criterion.
  // Per-drop DEMAND is still never attached here — that needs an explicit
  // Basis of Design / library assumption (Process Utilities brief).
  { value: "cda_drops", label: "CDA Drops (compressed dry air)" },
  { value: "n2_drops",  label: "N₂ Drops (nitrogen)" },
  { value: "vac_drops", label: "Vacuum Drops" },
  { value: "pw_drops",  label: "PW / DI Drops (purified water)" },
  // Deliberately NOT here: lighting_fixture_count / hepa counts. Those are
  // engineered quantities (roomSystemsEngine.js) — a placed symbol must never
  // silently redefine the requirement; the estimator adopts a drawn count as
  // an explicit override in the Room Detail Flyout instead.
];

// "Generated system" tag on a Count-style Item/Assembly (EstimateCostItems/
// Assemblies.AutoSystem, db/30): marks it as THE catalog entity the HEPA &
// Lighting engine generates on the canvas for that system (see
// src/utils/autoLayoutEngine.js resolveAutoSystemEntity — a Room Template's
// own tagged Standard Equipment row wins over the global default).
export const AUTO_SYSTEM_OPTIONS = [
  { value: "", label: "— none —" },
  { value: "hepa", label: "HEPA filter (generated from the HEPA engine)" },
  { value: "light", label: "Light fixture (generated from the lighting engine)" },
];

export function calcTypeUnit(style, value) {
  return CALC_TYPES_BY_STYLE[style]?.find((o) => o.value === value)?.unit || null;
}

// OST "Round Quantity / Round to" for a linear run — the measured length
// rounded to the nearest `roundToIn` inches (1 = nearest inch) before any
// quantity derives from it. Mirrors fn_RoundLengthToIn (db/37) exactly.
export function roundLengthToIn(lengthFt, roundToIn) {
  if (lengthFt == null || !(roundToIn > 0)) return lengthFt;
  const stepFt = roundToIn / 12;
  return Math.round(lengthFt / stepFt) * stepFt;
}

// The area twin: a drawn area's square feet rounded to the nearest `roundToSf` (1 = nearest SF, 5 = nearest 5 SF,
// 0.1 = a tenth), per shape, before any quantity derives from it. Mirrors fn_RoundAreaToSf (db/37) exactly.
export function roundAreaToSf(areaFt2, roundToSf) {
  if (areaFt2 == null || !(roundToSf > 0)) return areaFt2;
  return Math.round(areaFt2 / roundToSf) * roundToSf;
}

// One slot's value — mirrors fn_ConditionCalcQuantity's CASE exactly.
// `grid` is gridTileTakeoff()'s result (live, from the polygon) or null;
// tile_count_visible falls back to CEILING(average) without it, same as SQL.
export function calcQuantityValue(style, calcType, { primary, perimeterFt, segmentCount, heightFt, widthFt, thicknessIn, tileWFt, tileHFt, gapIn, grid }) {
  if (!calcType) return null;
  if (style === "area") {
    if (calcType === "area") return primary;
    if (calcType === "perimeter") return perimeterFt;
    if (calcType === "volume") return thicknessIn != null ? primary * (thicknessIn / 12) : null;
    const pitchFootprint = tileWFt > 0 && tileHFt > 0 ? (tileWFt + (gapIn || 0) / 12) * (tileHFt + (gapIn || 0) / 12) : null;
    if (calcType === "tile_count_average") return pitchFootprint ? primary / pitchFootprint : null;
    if (calcType === "tile_count_visible") {
      if (grid?.tileCountVisible != null) return grid.tileCountVisible;
      return pitchFootprint ? Math.ceil(primary / pitchFootprint) : null;
    }
    if (calcType === "grid_length_visible") return grid?.gridLengthVisibleFt ?? null;
    return null;
  }
  if (style === "linear") {
    if (calcType === "length") return primary;
    if (calcType === "segment_count") return segmentCount;
    if (calcType === "surface_single_side") return heightFt != null ? primary * heightFt : null;
    if (calcType === "surface_both_sides") return heightFt != null ? primary * heightFt * 2 : null;
    if (calcType === "surface_top_or_bottom") return widthFt != null ? primary * widthFt : null;
    if (calcType === "surface_top_and_bottom") return widthFt != null ? primary * widthFt * 2 : null;
    if (calcType === "surface_single_end") return heightFt != null && widthFt != null ? heightFt * widthFt : null;
    if (calcType === "surface_both_ends") return heightFt != null && widthFt != null ? heightFt * widthFt * 2 : null;
    if (calcType === "surface_all_side_duct") return heightFt != null && widthFt != null ? primary * 2 * (heightFt + widthFt) : null;
    if (calcType === "volume") return heightFt != null && thicknessIn != null ? primary * heightFt * (thicknessIn / 12) : null;
    return null;
  }
  if (style === "count") {
    if (calcType === "count") return primary;
    if (calcType === "perimeter_top_both_sides") return heightFt != null && widthFt != null ? heightFt * 2 + widthFt : null;
    if (calcType === "perimeter_4_sides") return heightFt != null && widthFt != null ? 2 * (heightFt + widthFt) : null;
    if (calcType === "surface_single_width_side") return heightFt != null && widthFt != null ? heightFt * widthFt : null;
    if (calcType === "surface_both_width_sides") return heightFt != null && widthFt != null ? heightFt * widthFt * 2 : null;
    return null;
  }
  return null;
}

// All populated slots for a placed/drawn condition row (as returned by
// sp_GetTakeoffConditionsDrawn — Style/Quantity1-3CalcType/Quantity1-3Unit/
// DefaultHeightFt/DefaultWidthFt/DefaultThicknessIn/HeightFtOverride/
// QuantityOverride/GeometryJson already resolved master-vs-revision-scoped).
// Returns [{ q, u }, ...] — same shape the canvas label rendering already expects.
/** The row's three quantity SLOTS, in order — [Q1, Q2, Q3], each { q, u } or null when that slot is unset / has no value. */
export function computeConditionQuantitySlots(c) {
  const style = c.Style;
  const heightFt = c.HeightFtOverride ?? c.DefaultHeightFt;
  const widthFt = c.DefaultWidthFt;
  const thicknessIn = c.DefaultThicknessIn;
  const geo = parseGeom(c);

  let primary = c.QuantityOverride;
  if (primary == null) {
    if (style === "count") primary = 1;
    else if (style === "area") primary = roundAreaToSf(geo?.areaFt2 ?? 0, c.RoundAreaToSf); // an explicit override above is never rounded
    else primary = roundLengthToIn(geo?.lengthFt ?? 0, c.RoundToIn); // an explicit override above is never rounded
  }
  const perimeterFt = style === "area" ? geo?.perimeterFt ?? null : null;
  const segmentCount = style === "linear" && geo?.points ? geo.points.length - 1 : null;
  // Live grid walk from the polygon when the type defines a tile grid; the
  // stored '$.grid' on the row (what SQL prices from) is only a fallback for
  // a row whose points somehow aren't available client-side.
  const tileWFt = c.GridTileWFt, tileHFt = c.GridTileHFt, gapIn = c.GridGapIn;
  const grid = style === "area" && tileWFt > 0 && tileHFt > 0
    ? (geo?.points?.length >= 3 ? gridTileTakeoff(geo.points, tileWFt, tileHFt, gapIn, { areaFt2: geo?.areaFt2 }) : geo?.grid ?? null)
    : null;

  const slots = [
    [c.Quantity1CalcType, c.Quantity1Unit],
    [c.Quantity2CalcType, c.Quantity2Unit],
    [c.Quantity3CalcType, c.Quantity3Unit],
  ];
  return slots.map(([calcType, unit]) => {
    if (!calcType) return null;
    const q = calcQuantityValue(style, calcType, { primary, perimeterFt, segmentCount, heightFt, widthFt, thicknessIn, tileWFt, tileHFt, gapIn, grid });
    return q != null ? { q, u: unit } : null;
  });
}

/** The row's quantities with the empty slots dropped (what the canvas labels, dock and Σ Totals show). */
export function computeConditionQuantities(c) {
  return computeConditionQuantitySlots(c).filter(Boolean);
}
